import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Drawer } from './Drawer'
import { StatusBadge } from './StatusBadge'
import {
  IconCopy,
  IconCheck,
  IconSecurity,
  IconOrganizations,
  IconDocuments,
  IconUsers,
  IconAlertTriangle,
} from './Icons'
import { useDocument } from '@/hooks/useRootQueries'
import { updateDocStatus, downloadDocumentFile } from '@/api/root'
import { toast } from '@/components/Toast'
import { exactTimestamp, relativeTime } from '@/utils/time'
import { formatHash } from '@/utils/format'
import type { AuditEvent, DocumentItem } from '@/types/api'

interface DocumentDetailDrawerProps {
  open: boolean
  onClose: () => void
  documentId?: string | null
  initialDocument?: DocumentItem | null
  event?: AuditEvent | null
}

export function DocumentDetailDrawer({
  open,
  onClose,
  documentId,
  initialDocument,
  event,
}: DocumentDetailDrawerProps) {
  const navigate = useNavigate()
  const [copiedId, setCopiedId] = useState(false)
  const [voiding, setVoiding] = useState(false)

  // Determine effective doc ID
  const effectiveDocId =
    documentId ||
    initialDocument?.id ||
    event?.documentId ||
    event?.document_id ||
    null

  const { data: fetchedDoc, isLoading, refetch } = useDocument(
    effectiveDocId ?? undefined,
  )

  const doc = fetchedDoc || initialDocument

  const title =
    doc?.title ||
    event?.documentTitle ||
    event?.document_title ||
    (effectiveDocId ? `Document #${effectiveDocId.slice(-6)}` : 'Incident Details')

  const effectiveStatus = (
    doc?.status ||
    (event?.action?.toLowerCase().includes('expire') ? 'expired' : 'sent')
  ).toLowerCase()

  const isExpired =
    effectiveStatus === 'expired' ||
    event?.action?.toLowerCase().includes('expire')

  const orgId = doc?.orgId || event?.orgId || event?.org_id
  const orgName =
    doc?.orgName ||
    event?.orgName ||
    event?.org_name ||
    (orgId ? `Org #${orgId.slice(-6)}` : undefined)

  const senderEmail =
    doc?.ownerEmail ||
    event?.actorEmail ||
    (event?.actor?.includes('@') ? event.actor : undefined) ||
    'Sender / Initiator'

  const senderName =
    (doc?.ownerEmail && doc.ownerEmail.split('@')[0]) ||
    event?.actorName ||
    event?.actor ||
    'Document Owner'

  function handleCopy(text: string, msg: string) {
    navigator.clipboard.writeText(text)
    setCopiedId(true)
    toast.success(msg)
    setTimeout(() => setCopiedId(false), 2000)
  }

  async function handleVoidDoc() {
    if (!effectiveDocId) return
    if (
      !window.confirm(
        `Are you sure you want to void "${title}"? This cannot be undone.`,
      )
    )
      return

    try {
      setVoiding(true)
      await updateDocStatus(effectiveDocId, 'voided')
      toast.success('Document marked as VOIDED')
      refetch()
    } catch {
      toast.error('Failed to void document')
    } finally {
      setVoiding(false)
    }
  }

  const drawerTitle = event
    ? 'Security Incident & Document Telemetry'
    : 'Document Details'

  return (
    <Drawer open={open} onClose={onClose} title={drawerTitle}>
      <div className="space-y-4 text-slate-800 text-xs">
        
        {/* Header Summary Card */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3.5 space-y-2.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <StatusBadge status={effectiveStatus} />
                {event && (
                  <span className="rounded-md bg-rose-50 border border-rose-200 px-2 py-0.5 font-mono text-[10px] font-bold text-rose-700">
                    Security Incident
                  </span>
                )}
              </div>
              <h3 className="text-sm font-bold text-slate-900 leading-snug break-words">
                {title}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700 border border-sky-200 shadow-2xs">
              <IconDocuments className="h-4 w-4" />
            </div>
          </div>

          {effectiveDocId && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-[11px]">
              <span className="text-slate-500 text-[10px] uppercase font-bold">Doc ID</span>
              <div className="flex items-center gap-1.5">
                <span className="text-slate-800 font-semibold select-all">
                  {effectiveDocId}
                </span>
                <button
                  onClick={() => handleCopy(effectiveDocId, 'Document ID copied!')}
                  className="rounded p-1 text-slate-400 hover:text-sky-700 hover:bg-sky-50"
                  title="Copy Document ID"
                >
                  {copiedId ? (
                    <IconCheck className="h-3 w-3 text-emerald-600" />
                  ) : (
                    <IconCopy className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="rounded-lg border border-slate-200 bg-white p-3 text-center text-slate-500 font-mono text-[11px] flex items-center justify-center gap-2">
            <span className="h-2 w-2 rounded-full bg-sky-600 animate-ping" />
            <span>Loading document details...</span>
          </div>
        )}

        {/* Expired / Voided Banner */}
        {isExpired && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 flex items-start gap-2.5">
            <IconAlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-amber-900 text-xs">Signing Window Expired</div>
              <p className="mt-0.5 text-[11px] text-amber-800 leading-normal">
                This document exceeded its deadline. Recipients can no longer access or sign it.
              </p>
              {doc?.expiryDate && (
                <div className="mt-1.5 font-mono text-[10.5px] font-semibold text-amber-900">
                  Expired on: {exactTimestamp(doc.expiryDate)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tenant Organization Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 space-y-2">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <span className="font-bold text-slate-700 uppercase tracking-wider text-[10.5px] flex items-center gap-1.5">
              <IconOrganizations className="h-3.5 w-3.5 text-sky-600" />
              <span>Organization</span>
            </span>
            {orgId && (
              <Link
                to={`/organizations/${orgId}`}
                className="font-mono text-[11px] font-semibold text-sky-600 hover:text-sky-800 hover:underline"
              >
                Manage Org →
              </Link>
            )}
          </div>
          <div className="space-y-1.5 font-mono text-[11.5px]">
            <div className="flex justify-between items-center py-0.5">
              <span className="text-slate-500 font-sans">Name:</span>
              <span className="font-bold text-slate-900 font-sans">{orgName || 'Platform Tenant'}</span>
            </div>
            {orgId && (
              <div className="flex justify-between items-center py-0.5">
                <span className="text-slate-500 font-sans">Org ID:</span>
                <span className="text-slate-700 select-all">{orgId}</span>
              </div>
            )}
          </div>
        </div>

        {/* Sender Info Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 space-y-2">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <span className="font-bold text-slate-700 uppercase tracking-wider text-[10.5px] flex items-center gap-1.5">
              <IconUsers className="h-3.5 w-3.5 text-sky-600" />
              <span>Sender / Initiator</span>
            </span>
            <span className="text-slate-400 font-mono text-[10px]">Originator</span>
          </div>
          <div className="space-y-1.5 font-mono text-[11.5px]">
            <div className="flex justify-between items-center py-0.5">
              <span className="text-slate-500 font-sans">Sender:</span>
              <div className="text-right font-sans">
                <div className="font-bold text-slate-900">{senderName}</div>
                {senderEmail && senderEmail !== senderName && (
                  <div className="font-mono text-[10.5px] text-slate-500">{senderEmail}</div>
                )}
              </div>
            </div>
            {doc?.createdAt && (
              <div className="flex justify-between items-center py-0.5 border-t border-slate-100">
                <span className="text-slate-500 font-sans">Sent Date:</span>
                <span className="text-slate-800 font-semibold">{exactTimestamp(doc.createdAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Recipients / Signers Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 space-y-2.5">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <span className="font-bold text-slate-700 uppercase tracking-wider text-[10.5px] flex items-center gap-1.5">
              <span>Recipients / Signers</span>
              {Array.isArray(doc?.signers) && doc.signers.length > 0 && (
                <span className="text-slate-400 font-normal">({doc.signers.length})</span>
              )}
            </span>
            <span className="text-slate-400 font-mono text-[10px]">Routing</span>
          </div>

          {Array.isArray(doc?.signers) && doc.signers.length > 0 ? (
            <div className="space-y-2">
              {doc.signers.map((s, idx) => {
                const sStatus = s.status.toLowerCase()
                const isSigned = sStatus === 'signed'
                const isDeclined = sStatus === 'declined'

                return (
                  <div
                    key={s.id || idx}
                    className="rounded-lg border border-slate-200/90 bg-slate-50/60 p-2.5 space-y-1.5"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded bg-white border border-slate-200 font-mono text-[10px] font-bold text-slate-700 shadow-2xs">
                          #{s.order || idx + 1}
                        </span>
                        <div>
                          <div className="font-bold text-slate-900 font-sans text-xs">
                            {s.name || 'Signer'}
                          </div>
                          <div className="font-mono text-[10.5px] text-slate-500">{s.email}</div>
                        </div>
                      </div>
                      <StatusBadge
                        status={isExpired && !isSigned && !isDeclined ? 'expired' : s.status}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-1 border-t border-slate-200/60 pt-1.5 font-mono text-[10.5px] text-slate-600">
                      {s.signedAt && (
                        <div className="flex justify-between">
                          <span className="text-slate-400 font-sans">Signed:</span>
                          <span className="font-semibold text-slate-800">{exactTimestamp(s.signedAt)}</span>
                        </div>
                      )}
                      {s.ipAddress && (
                        <div className="flex justify-between">
                          <span className="text-slate-400 font-sans">IP Address:</span>
                          <span className="text-slate-700">{s.ipAddress}</span>
                        </div>
                      )}
                      {s.signatureHash && (
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400 font-sans">Hash:</span>
                          <span
                            className="bg-white border border-slate-200 text-sky-800 px-1.5 py-0.5 rounded cursor-pointer select-all font-semibold"
                            title={`Click to copy hash:\n${s.signatureHash}`}
                            onClick={() => handleCopy(s.signatureHash || '', 'Signature hash copied!')}
                          >
                            {formatHash(s.signatureHash, 6, 6)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5 font-mono text-[11px] text-slate-600">
              <div>Signer: {senderEmail}</div>
              <div className="text-slate-400 text-[10px] mt-0.5">Status: {effectiveStatus}</div>
            </div>
          )}
        </div>

        {/* Security Incident Context (if opened from Incident event) */}
        {event && (
          <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-3.5 space-y-2">
            <div className="flex items-center justify-between border-b border-rose-200/60 pb-1.5">
              <span className="font-bold text-rose-900 uppercase tracking-wider text-[10.5px] flex items-center gap-1.5">
                <IconSecurity className="h-3.5 w-3.5 text-rose-600" />
                <span>Security Incident Details</span>
              </span>
              <span className="font-mono text-[10px] text-rose-700 font-semibold">Audit Event</span>
            </div>
            <div className="space-y-1.5 font-mono text-[11px]">
              <div className="flex justify-between py-0.5">
                <span className="text-slate-500 font-sans">Incident:</span>
                <span className="font-bold text-rose-800 font-sans">{event.action}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-slate-500 font-sans">Logged At:</span>
                <span className="text-slate-800 font-semibold">{exactTimestamp(event.timestamp)} ({relativeTime(event.timestamp)})</span>
              </div>
              {event.ipAddress && (
                <div className="flex justify-between py-0.5">
                  <span className="text-slate-500 font-sans">Client IP:</span>
                  <span className="text-slate-800 font-semibold">{event.ipAddress}</span>
                </div>
              )}
              {event.id && (
                <div className="flex justify-between py-0.5 border-t border-rose-100 pt-1">
                  <span className="text-slate-500 font-sans">Event ID:</span>
                  <span className="text-slate-700 select-all">{formatHash(event.id, 6, 6)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="space-y-2 pt-2 border-t border-slate-200">
          {effectiveDocId && (
            <button
              onClick={async () => {
                try {
                  toast.info('Downloading document...')
                  await downloadDocumentFile(effectiveDocId, `${title}.pdf`)
                  toast.success('Document downloaded successfully')
                } catch (err: any) {
                  toast.error(err?.message || 'Download failed')
                }
              }}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-600 py-2.5 text-xs font-bold text-white shadow-xs hover:bg-emerald-700 transition-all"
            >
              <span>📥</span>
              <span>Download Document PDF</span>
            </button>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                onClose()
                if (effectiveDocId || title) {
                  navigate(
                    `/documents?search=${encodeURIComponent(title || effectiveDocId || '')}`,
                  )
                } else {
                  navigate('/documents')
                }
              }}
              className="flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white py-2 text-xs font-bold text-slate-700 shadow-xs hover:bg-slate-50 transition-all"
            >
              <span>Documents Table</span>
            </button>

            {effectiveDocId && (
              <button
                onClick={() => {
                  onClose()
                  navigate(
                    `/activity?search=${encodeURIComponent(effectiveDocId)}`,
                  )
                }}
                className="flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white py-2 text-xs font-bold text-slate-700 shadow-xs hover:bg-slate-50 transition-all"
              >
                <span>Activity Feed</span>
              </button>
            )}
          </div>

          {effectiveDocId && effectiveStatus !== 'voided' && (
            <button
              onClick={handleVoidDoc}
              disabled={voiding}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 py-2 text-xs font-bold text-rose-700 shadow-xs hover:bg-rose-100 transition-all disabled:opacity-50"
            >
              <span>{voiding ? 'Voiding Document...' : 'Void Document'}</span>
            </button>
          )}
        </div>

      </div>
    </Drawer>
  )
}
